'use client';

import { useState } from 'react';

import { useCreateAssignmentMutation } from '../services/handover-scanning.mutations';
import { ReceivingListPanel } from './receiving-list-panel';
import { ScannerSelectionPanel } from './scanner-selection-panel';
import { OperatorSelectionPanel } from './operator-selection-panel';
import { AssignmentConfirmFooter } from './assignment-confirm-footer';

export function ContainerAssignmentTab() {
  // ── State ────────────────────────────────────────────────────────────────
  const [selectedContainerIds, setSelectedContainerIds] = useState<number[]>(
    []
  );
  const [selectedScannerId, setSelectedScannerId] = useState<number | null>(
    null
  );
  const [selectedScannerName, setSelectedScannerName] = useState<string | null>(
    null
  );
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(
    null
  );
  const [selectedOperatorName, setSelectedOperatorName] = useState<
    string | null
  >(null);

  const isConfirmDisabled =
    selectedContainerIds.length === 0 ||
    selectedScannerId === null ||
    selectedOperatorId === null;

  // ── Mutation ─────────────────────────────────────────────────────────────
  const createAssignmentMutation = useCreateAssignmentMutation();

  // ── Handlers ─────────────────────────────────────────────────────────────
  function handleToggleContainer(id: number) {
    setSelectedContainerIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  }

  function handleSelectAll(ids: number[]) {
    setSelectedContainerIds(ids);
  }

  function handleClearSelection() {
    setSelectedContainerIds([]);
  }

  function handleSelectScanner(id: number, name: string) {
    setSelectedScannerId(id);
    setSelectedScannerName(name);
  }

  function handleSelectOperator(id: number, name: string) {
    setSelectedOperatorId(id);
    setSelectedOperatorName(name);
  }

  async function handleConfirm() {
    if (isConfirmDisabled) return;

    try {
      await createAssignmentMutation.mutateAsync({
        scanning_container_ids: selectedContainerIds,
        device_id: selectedScannerId!,
        operator_id: selectedOperatorId!
      });

      // Reset state after successful assignment
      setSelectedContainerIds([]);
      setSelectedScannerId(null);
      setSelectedScannerName(null);
      setSelectedOperatorId(null);
      setSelectedOperatorName(null);
    } catch {
      // handled by global MutationCache
    }
  }

  return (
    <div className='flex flex-col pb-24'>
      {/* Three-column grid */}
      <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
        <ReceivingListPanel
          selectedContainerIds={selectedContainerIds}
          onToggleContainer={handleToggleContainer}
          onSelectAll={handleSelectAll}
          onClearSelection={handleClearSelection}
        />

        <ScannerSelectionPanel
          selectedScannerId={selectedScannerId}
          onSelectScanner={handleSelectScanner}
          selectedContainerCount={selectedContainerIds.length}
        />

        <OperatorSelectionPanel
          selectedOperatorId={selectedOperatorId}
          onSelectOperator={handleSelectOperator}
        />
      </div>

      {/* Sticky footer */}
      <AssignmentConfirmFooter
        selectedContainerCount={selectedContainerIds.length}
        selectedScanner={selectedScannerName}
        selectedOperator={selectedOperatorName}
        disabled={isConfirmDisabled}
        onConfirm={handleConfirm}
        isConfirming={createAssignmentMutation.isPending}
      />
    </div>
  );
}
